import { useForm } from '@tanstack/react-form'
import { createFileRoute } from '@tanstack/react-router'
import { useModulesGetModuleByModuleId, useModulesSubmitModuleResult } from '@unsw/api/generated'
import { useAuthStore } from '@unsw/store/auth'
import { Button } from '@unsw/ui/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@unsw/ui/components/ui/card'
import { Label } from '@unsw/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@unsw/ui/components/ui/select'
import { toast } from '@unsw/ui/components/ui/sonner'
import { Loader2 } from 'lucide-react'

export const Route = createFileRoute('/dashboard/profile/preferred-accent')({
  component: PreferredAccentPage,
})

interface Accent {
  code: string
  name: string
  flag_image: string
}

function PreferredAccentPage() {
  const { user, setUser } = useAuthStore()

  // Fetch available accents from module config
  const {
    data: moduleData,
    isLoading: isLoadingAccents,
    isError,
  } = useModulesGetModuleByModuleId('PreferredAccentModule', undefined, {
    query: {
      staleTime: Number.POSITIVE_INFINITY,
      retry: false,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    },
  })

  // Type-narrow to get accents from successful response
  const moduleConfig = moduleData?.status === 200 && moduleData.data?.config
  const accents: Accent[] = (moduleConfig && (moduleConfig as { accents?: Accent[] }).accents) || []

  const submitModuleMutation = useModulesSubmitModuleResult()

  const form = useForm({
    defaultValues: {
      accent: user?.accent || '',
      voiceType: user?.voice_gender || 'MALE',
    },
    onSubmit: async ({ value }) => {
      try {
        await submitModuleMutation.mutateAsync({
          data: {
            id: 'PreferredAccentModule',
            data: {
              preferred_accent: value.accent,
              preferred_voice_type: value.voiceType,
            },
          },
        })
        // Update local user state
        if (user) {
          setUser({
            ...user,
            accent: value.accent,
            voice_gender: value.voiceType,
          })
        }
        toast.success('Accent preferences saved')
      } catch {
        toast.error('Failed to save accent preferences')
      }
    },
  })

  const isLoading = submitModuleMutation.isPending

  if (isLoadingAccents && !moduleData && !isError) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Accent Settings</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
          className="space-y-6"
        >
          {/* Accent Selection */}
          <form.Field name="accent">
            {(field) => (
              <div className="space-y-2">
                <Label>Choose an accent</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an accent for feedback" />
                  </SelectTrigger>
                  <SelectContent>
                    {accents.length === 0 ? (
                      <div className="px-2 py-1.5 text-sm text-muted-foreground">No options</div>
                    ) : (
                      accents.map((accent) => (
                        <SelectItem key={accent.code} value={accent.code}>
                          <span className="flex items-center gap-2">
                            {accent.flag_image && (
                              <img
                                src={accent.flag_image}
                                alt={accent.name}
                                className="w-5 h-4 object-cover"
                              />
                            )}
                            {accent.name}
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          {/* Voice Type Selection */}
          <form.Field name="voiceType">
            {(field) => (
              <div className="space-y-2">
                <Label>Choose feedback voice type</Label>
                <Select
                  value={field.state.value}
                  onValueChange={(value) => field.handleChange(value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select voice type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MALE">Male</SelectItem>
                    <SelectItem value="FEMALE">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </form.Field>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="ghost" onClick={() => form.reset()} disabled={isLoading}>
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Save Profile
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
